import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { AppSnackbar } from '../services/AppSnackbar';
import { RootState } from './types';
import { getProductDisplayName } from '../utils/productDisplayName';

export interface CartItem {
  productID: string;
  variantId?: string;
  Quantity?: number; //cart quantity
  quantity?: number;
  category?: string;
  itemname?: string;
  mass?: number;
  unitofmass?: string;
  model?: string;
  size?: string;
  image?: string;
  price: number;
  bp_order_completed?: boolean;
  bp_order_paid_quantity?: number;
  bp_order_paid_mass?: number;
  bp_sale_original_quantity?: number;
  price_type?: string;
  bp_selling_price_changed?: boolean;
}
interface CartOrderData {
  order: orderData[];
  orderType: string;
  customerName: string;
  orderID: string;
}
export interface orderData {
  bp_order_mass: number;
  bp_order_paid_mass: number;
  bp_order_quantity: number;
  bp_order_completed: boolean;
  bp_order_paid_quantity: number;
  bp_product_information: {
    bp_minimum_stock_level: number;
    bp_product_category: string;
    bp_product_mass: string;
    bp_product_model: string;
    bp_product_name: string;
    bp_product_quantity: number;
    bp_product_selling_price: number;
    bp_product_size: string;
    bp_product_price_type: 'unit' | 'item';
    bp_product_sub_category: string;
    bp_product_unit_of_mass: string;
    bp_variant_id?: string;
    bp_variant_attributes?: Record<string, string>;
    bp_product_has_variants?: boolean;
    bp_product_variant_attributes?: string[];
    bp_product_parent_name?: string;
    _id: string;
  };
}

export interface CartSliceObject {
  cartItems: CartItem[];
  cartOrderData: CartOrderData;
  cartTotalQuantity: number;
  cartTotalAmount: number;
  updatingCartitems: boolean;
}

const initialState: CartSliceObject = {
  cartItems: [],
  cartOrderData: {
    order: [],
    orderType: '',
    customerName: '',
    orderID: '',
    // ReceiptID: "",
  },
  cartTotalQuantity: 0,
  cartTotalAmount: 0,
  updatingCartitems: false,
};

const calculateTotals = (cartItems: CartItem[]) => {
  return cartItems.reduce(
    (cartTotal, cartItem) => {
      const {
        price,
        Quantity,
        price_type,
        mass,
        bp_order_paid_quantity,
        bp_order_paid_mass,
      } = cartItem;

      if (price_type === 'unit') {
        // For unit pricing, use mass multiplied by price
        let itemMass = (mass ?? 0) - (bp_order_paid_mass ?? 0);
        itemMass = itemMass < 0 ? 0 : itemMass;
        const itemTotal = price * itemMass;
        if (itemMass > 0) {
          cartTotal.quantity += 1;
        }
        cartTotal.total += itemTotal;
      } else {
        // Default to item pricing (original behavior)
        const currentQuantity = (Quantity ?? 0) - (bp_order_paid_quantity ?? 0);
        const itemTotal = price * currentQuantity;
        cartTotal.total += itemTotal;
        cartTotal.quantity += currentQuantity;
      }

      return cartTotal;
    },
    { total: 0, quantity: 0 },
  );
};

const showToast = (
  itemname: string,
  mass: string = '',
  unitofmass: string = '',
  model: string = '',
  size: string = '',
) => {
  AppSnackbar.show(
    `${getProductDisplayName({ itemname, mass, unitofmass, model, size })} is out of stock`,
    'warning',
    { durationMs: 4500 },
  );
};

const sameCartItemIdentity = (
  cartItem: CartItem,
  target: { productID: string; variantId?: string },
) =>
  cartItem.productID === target.productID &&
  (cartItem.variantId ?? null) === (target.variantId ?? null);

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<CartItem>) => {
      const itemIndex = state.cartItems.findIndex(
        cartitem => sameCartItemIdentity(cartitem, action.payload),
      );
      //update the quantity of the item in the basket
      if (action.payload.price_type === 'item') {
        if (itemIndex >= 0) {
          if ((action.payload.quantity ?? 0) > 0) {
            if (
              (state.cartItems[itemIndex].Quantity ?? 0) <
              (action.payload.quantity ?? 0) +
                (state.cartItems[itemIndex].bp_sale_original_quantity ?? 0)
            ) {
              state.cartItems[itemIndex].Quantity =
                (state.cartItems[itemIndex].Quantity ?? 0) + 1;
            } else {
              showToast(
                state.cartItems[itemIndex].itemname ?? '',
                state.cartItems[itemIndex].mass?.toString() ?? '',
                state.cartItems[itemIndex].unitofmass ?? '',
                state.cartItems[itemIndex].model ?? '',
                state.cartItems[itemIndex].size ?? '',
              );
            }
          } else {
            showToast(
              state.cartItems[itemIndex].itemname ?? '',
              state.cartItems[itemIndex].mass?.toString() ?? '',
              state.cartItems[itemIndex].unitofmass ?? '',
              state.cartItems[itemIndex].model ?? '',
              state.cartItems[itemIndex].size ?? '',
            );
          }
        } else {
          const tempProduct = { ...action.payload, Quantity: 1 } as CartItem;
          state.cartItems.push(tempProduct);
        }
      } else if (action.payload.price_type === 'unit') {
        if (itemIndex >= 0) {
          if ((action.payload.mass ?? 0) >= 0) {
            state.cartItems[itemIndex].mass = action.payload.mass ?? 0;
          }
        } else {
          const tempProduct = {
            ...action.payload,
            mass: action.payload.mass ?? 0,
          } as CartItem;
          state.cartItems.push(tempProduct);
        }
      }
    },
    addToCartQuantity: (
      state,
      action: PayloadAction<{
        productID: string;
        variantId?: string;
        quantity: number;
        product?: CartItem;
      }>,
    ) => {
      const itemIndex = state.cartItems.findIndex(
        cartitem => sameCartItemIdentity(cartitem, action.payload),
      );
      if (itemIndex >= 0) {
        state.cartItems[itemIndex].Quantity = action.payload.quantity;
        return;
      }
      if (action.payload.product) {
        state.cartItems.push({
          ...action.payload.product,
          Quantity: action.payload.quantity,
        });
      }
    },
    updateCartItemSellingPrice: (
      state,
      action: PayloadAction<{
        productID: string;
        variantId?: string;
        sellingPrice: number;
      }>,
    ) => {
      const itemIndex = state.cartItems.findIndex(
        cartitem => sameCartItemIdentity(cartitem, action.payload),
      );
      if (itemIndex >= 0) {
        const cartItem = state.cartItems[itemIndex];

        // Guardrail: Prevent price editing if product has been paid for
        // For item-type products: if bp_order_paid_quantity >= 1, no price change allowed
        // For unit-type products: if bp_order_paid_mass > 0, no price change allowed
        // Note: Location setting check (bp_location_allow_price_edit_during_checkout) is handled in the component before dispatching
        const isItemType = cartItem.price_type === 'item';
        const hasPaidQuantity = (cartItem.bp_order_paid_quantity ?? 0) >= 1;
        const hasPaidMass = (cartItem.bp_order_paid_mass ?? 0) > 0;

        if (isItemType && hasPaidQuantity) {
          console.warn(
            `Cannot edit selling price: Product ${cartItem.productID} has paid quantity >= 1. Price editing is disabled to preserve sale integrity.`,
          );
          AppSnackbar.show(
            'Cannot edit selling price: Product has already been paid for',
            'error',
            { durationMs: 4000 },
          );
          return;
        }

        if (!isItemType && hasPaidMass) {
          console.warn(
            `Cannot edit selling price: Product ${cartItem.productID} has paid mass > 0. Price editing is disabled to preserve sale integrity.`,
          );
          AppSnackbar.show(
            'Cannot edit selling price: Product has already been paid for',
            'error',
            { durationMs: 4000 },
          );
          return;
        }

        state.cartItems[itemIndex].price = action.payload.sellingPrice;
        state.cartItems[itemIndex].bp_selling_price_changed = true;
      }
    },
    addToCartOrderData: (state, action: PayloadAction<CartOrderData>) => {
      state.cartOrderData = action.payload;
    },
    clearCartOrderData: state => {
      state.cartOrderData = initialState.cartOrderData;
    },
    removeCartItem: (
      state,
      action: PayloadAction<{ productID: string; variantId?: string }>,
    ) => {
      const newCartItems = state.cartItems.filter(
        cartItem => !sameCartItemIdentity(cartItem, action.payload),
      );
      state.cartItems = newCartItems;

      const { total, quantity } = calculateTotals(newCartItems);
      console.log('calculateTotals', total, quantity);
      state.cartTotalAmount = total;
      state.cartTotalQuantity = quantity;
    },
    DecreaseCart: (
      state,
      action: PayloadAction<{ productID: string; variantId?: string }>,
    ) => {
      const itemIndex = state.cartItems.findIndex(
        item => sameCartItemIdentity(item, action.payload),
      );
      if (itemIndex < 0) return;
      if ((state.cartItems[itemIndex]?.Quantity ?? 0) > 1) {
        state.cartItems[itemIndex].Quantity =
          (state.cartItems[itemIndex]?.Quantity ?? 0) - 1;
      } else if ((state.cartItems[itemIndex]?.Quantity ?? 0) <= 1) {
        const newCartItem = state.cartItems.filter(
          cartItem => !sameCartItemIdentity(cartItem, action.payload),
        );
        state.cartItems = newCartItem;
      }
    },
    ClearCart: state => {
      state.cartItems = initialState.cartItems;
      state.cartTotalQuantity = initialState.cartTotalQuantity;
      state.cartTotalAmount = initialState.cartTotalAmount;
    },
    getTotals: state => {
      const { total, quantity } = calculateTotals(state.cartItems);
      state.cartTotalAmount = total;
      state.cartTotalQuantity = quantity;
    },
    LoadToCart: (state, action: PayloadAction<CartItem[]>) => {
      const cartLength = action.payload.length;
      if (state.cartItems.length === 0) {
        state.cartItems = action.payload;
        state.cartTotalQuantity = cartLength;
      }
    },
    setUpdatingCartitems: (state, action: PayloadAction<boolean>) => {
      state.updatingCartitems = action.payload;
    },
  },
});

export const {
  addToCart,
  addToCartOrderData,
  clearCartOrderData,
  removeCartItem,
  getTotals,
  ClearCart,
  addToCartQuantity,
  updateCartItemSellingPrice,
  LoadToCart,
  DecreaseCart,
  setUpdatingCartitems,
} = cartSlice.actions;

// Basic memoized selectors
export const selectCartItems = (state: RootState) => state.cart.cartItems;

export const selectCartOrderData = (state: RootState) =>
  state.cart.cartOrderData;

export const selectUpdatingCartItems = (state: RootState) =>
  state.cart.updatingCartitems;

// Memoized cart totals calculation
export const selectCartTotals = createSelector([selectCartItems], cartItems => {
  return cartItems.reduce(
    (cartTotal, cartItem) => {
      const {
        price,
        Quantity,
        price_type,
        mass,
        bp_order_paid_quantity,
        bp_order_paid_mass,
      } = cartItem;

      if (price_type === 'unit') {
        let itemMass = (mass ?? 0) - (bp_order_paid_mass ?? 0);
        itemMass = itemMass < 0 ? 0 : itemMass;
        const itemTotal = price * itemMass;
        if (itemMass > 0) {
          cartTotal.quantity += 1;
        }
        cartTotal.total += itemTotal;
      } else {
        const currentQuantity = (Quantity ?? 0) - (bp_order_paid_quantity ?? 0);
        const itemTotal = price * currentQuantity;
        cartTotal.total += itemTotal;
        cartTotal.quantity += currentQuantity;
      }

      return cartTotal;
    },
    { total: 0, quantity: 0 },
  );
});

// Memoized cart total amount
export const selectCartTotalAmount = createSelector(
  [selectCartTotals],
  totals => totals.total,
);

// Memoized cart total quantity
export const selectCartTotalQuantity = createSelector(
  [selectCartTotals],
  totals => totals.quantity,
);

// Memoized cart items with valid quantities (for display)
export const selectValidCartItems = createSelector(
  [selectCartItems],
  cartItems => {
    return cartItems.filter(item => {
      if (item.price_type === 'unit') {
        const itemMass = (item.mass ?? 0) - (item.bp_order_paid_mass ?? 0);
        return itemMass > 0;
      } else {
        const currentQuantity =
          (item.Quantity ?? 0) - (item.bp_order_paid_quantity ?? 0);
        return currentQuantity > 0;
      }
    });
  },
);

// Memoized cart item count
export const selectCartItemCount = createSelector(
  [selectValidCartItems],
  validItems => validItems.length,
);

export default cartSlice.reducer;
